<template>
  <div class="other-info-container">
    <transition name="view" mode="out-in">
      <c-loader v-if="loading" :size="isMobile ? 79 : 99" />

      <div v-else class="body">
        <c-title v-if="!isMobile" dark-text>
          Outras Informações
        </c-title>

        <c-form-builder
          ref="form"
          :schema="formSchema"
          :disabled="submitting"
          :label-left="!isMobile"
          v-model="formData"
        />
      </div>
    </transition>
  </div>
</template>

<script>
import { computed, getCurrentInstance } from 'vue'
import { mapState, mapActions } from 'vuex'
import { MediaQuery } from '@convenia/mixins'
import useVue3Migration from '@convenia/composables/useVue3Migration'
import * as types from '@types'
import useSystemFields from '@convenia/employee-organisms/SystemFields/composables/useSystemFields'

export default {
  name: 'OtherInfo',

  compatConfig: { MODE: 3 },

  mixins: [ MediaQuery ],

  emits: [ 'next', 'previous' ],

  setup () {
    const { proxy } = getCurrentInstance()
    const { isVue2 } = useVue3Migration()

    const { formSchema, getFormValues } = useSystemFields({
      schema: computed(() => ({})),
      customFields: computed(() => proxy.customFields || {}),
    })

    return {
      isVue2,
      formSchema,
      getFormValues,
    }
  },

  computed: {
    ...mapState({
      customFields: state => state.otherInfo.customFields,
      additional: state => state.otherInfo.additional
    }),

    submitting: {
      get () { return this.$store.state.admission.submitting },
      set (value) { this.$store.commit(types.ADMISSION_SUBMITTING, value) }
    }
  },

  data: () => ({
    loading: true,
    formData: {}
  }),

  mounted () {
    this.init()
  },

  methods: {
    ...mapActions({
      getSystemFields: types.EMPLOYEE_OTHER_INFO_SYSTEM_FIELDS,
      getAdditionalInfo: types.EMPLOYEE_OTHER_INFO,
      setAdditionalInfo: types.EMPLOYEE_SET_OTHER_INFO
    }),

    next () {
      return this.onSubmit()
    },

    previous () {
      return this.onPrevious()
    },

    async init () {
      await Promise.all([
        this.getSystemFields(),
        this.getAdditionalInfo(),
      ])

      this.formData = this.getFormValues({}, this.additional)

      this.loading = false
    },

    async validateForm () {
      const { form } = this.$refs || {}
      form.hasValidation = true

      // CFormBuilder.next has no veeObserver, its validate() resolves { valid }
      const isValid = this.isVue2
        ? await form.$refs.veeObserver.validate()
        : (await form.validate()).valid

      if (!isValid) form.focusFirstError()

      return isValid
    },

    async onSubmit () {
      const valid = await this.validateForm()

      if (!valid) return

      this.submitting = true
      const [ err ] = await this.setAdditionalInfo(this.formData)
      this.submitting = false

      if (err)
        return this.$feedback.add({
          type: 'error',
          message: 'Erro',
          highlighted: 'ao salvar Outras Informações. Tente novamente.'
        })

      this.$feedback.add({
        type: 'success',
        message: 'Outras Informações',
        highlighted: 'atualizadas com sucesso'
      })

      this.$emit('next')
    },

    onPrevious () {
      this.$emit('previous')
    }
  }
}
</script>

<style lang="scss">
.other-info-container {
  position: relative;
  padding-bottom: 40px;
  $input-height: 40px;
  $label-width: 210px;

  & > .body {
    & > .c-title { padding: 20px; }

    & > .c-form-builder {
      margin-bottom: 10px;
      & > .c-shadowed, & > .c-shadowed > .wrapper { overflow: initial; }
      & > .actions { display: none; }

      .c-form-field.-label-left {
        grid-template-columns: 190px 360px;

        & > .input-wrapper > .hint {
          @include desktop { max-width: 190px; }
          @include tablet { display: none; }
        }
      }
    }
  }

  @include responsive (xs-mobile, mobile) {
    & > .body {
      & > .c-form-builder { margin-bottom: 0; }
    }
  }

  @include responsive (tablet, desktop) {
    margin-top: 10px;
    background: white;
    border-radius: 5px;
    box-shadow: 2px 8px 16px 0px rgba(0, 0, 0, 0.06);

    & > .c-loader { margin-top: 60px !important; }
  }
}
</style>
